import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Users,
  Wrench,
} from 'lucide-react';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import { JsonLd } from '@/components/JsonLd';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { BASE_URL, getHreflangLinks } from '@/seo/meta';
import {
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateOrganizationSchema,
  generateServiceSchema,
  generateWebsiteSchema,
} from '@/lib/seo';
import { trackCTA, trackServiceView } from '@/lib/analytics';
import { trackScroll } from '@/lib/scroll';
import { getServiceMeta } from '@/seo/meta';

const serviceName = 'Κατασκευή Web & Mobile Εφαρμογών';
const serviceSlug = 'apps';

type Locale = 'en' | 'el';

type HeroContent = {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: string;
  secondaryCta: string;
};

type SectionCopy = {
  eyebrow?: string;
  heading: string;
  description: string;
};

type BulletSection = SectionCopy & {
  bullets: string[];
};

type AppTypesContent = SectionCopy & {
  rightTitle: string;
  bullets: string[];
};

type ProcessStep = {
  title: string;
  description: string;
  bullets: string[];
};

type FaqItem = {
  question: string;
  answer: string;
};

type ClosingContent = {
  eyebrow?: string;
  title: string;
  description?: string;
  primaryCta: string;
};

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'App Development',
    title: 'Web & Mobile App Development',
    description:
      'We build applications that solve real problems and work reliably in practice. We focus on functionality, speed, and ease of use so the app can be used immediately and grow with your business.',
    primaryCta: 'Free consultation',
    secondaryCta: 'See our work',
  },
  el: {
    eyebrow: 'Υπηρεσίες Εφαρμογών',
    title: 'Κατασκευή Web & Mobile Εφαρμογών',
    description:
      'Αναπτύσσουμε εφαρμογές που λύνουν πραγματικά προβλήματα και δουλεύουν σωστά στην πράξη. Εστιάζουμε στη λειτουργικότητα, την ταχύτητα και την ευκολία χρήσης, ώστε η εφαρμογή να μπορεί να χρησιμοποιηθεί άμεσα και να εξελιχθεί μαζί με την επιχείρησή σας.',
    primaryCta: 'Δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε έργα μας',
  },
};

const appTypesCopy: Record<Locale, AppTypesContent> = {
  en: {
    eyebrow: 'What we build',
    heading: 'What kinds of apps we build',
    description: 'We build apps for businesses that need more than a simple website.',
    rightTitle: 'When does an app make sense',
    bullets: [
      'Client, appointment, or data management',
      'Internal organization for teams or staff',
      'Role-based user access',
      'Gradual evolution with new features',
    ],
  },
  el: {
    eyebrow: 'Τι φτιάχνουμε',
    heading: 'Τι είδους εφαρμογές φτιάχνουμε',
    description: 'Αναπτύσσουμε εφαρμογές για επιχειρήσεις που θέλουν κάτι περισσότερο από μια απλή ιστοσελίδα.',
    rightTitle: 'Πότε έχει νόημα μια εφαρμογή',
    bullets: [
      'Διαχείριση πελατών, ραντεβού ή δεδομένων',
      'Εσωτερική οργάνωση ομάδας ή προσωπικού',
      'Πρόσβαση χρηστών με δικαιώματα',
      'Σταδιακή εξέλιξη με νέες λειτουργίες',
    ],
  },
};

const processIntroCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'How we work',
    heading: 'How we work',
    description: "We don't start with code. We start with understanding.",
  },
  el: {
    eyebrow: 'Πώς δουλεύουμε',
    heading: 'Πώς δουλεύουμε',
    description: 'Δεν ξεκινάμε με κώδικα. Ξεκινάμε με κατανόηση.',
  },
};

const processCopy: Record<Locale, ProcessStep[]> = {
  en: [
    {
      title: 'Discovery & planning',
      description:
        'We discuss what you want to achieve, who will use the app, and what the essential first step is.',
      bullets: ['Goal definition', 'Simple feature outline', 'Clear delivery plan'],
    },
    {
      title: 'User experience design',
      description: 'We design the app to be simple, clean, and easy to use on every device.',
      bullets: ['Screens and user flows', 'Design adapted for mobile and desktop', 'Early feedback before development'],
    },
    {
      title: 'Development & testing',
      description: 'We build the application with a focus on stability and performance.',
      bullets: ['Clean, organized code', 'Functional checks before launch', 'Security and proper data handling'],
    },
    {
      title: 'Delivery & support',
      description: 'The app is delivered ready to use and we show you how to manage it.',
      bullets: ['Delivered ready-to-use app', 'Basic training', 'Post-launch support'],
    },
  ],
  el: [
    {
      title: 'Κατανόηση & σχεδιασμός',
      description:
        'Συζητάμε τι θέλετε να πετύχετε, ποιοι θα χρησιμοποιούν την εφαρμογή και ποιο είναι το απαραίτητο πρώτο βήμα.',
      bullets: ['Καθορισμός στόχων', 'Απλή περιγραφή λειτουργιών', 'Σαφές πλάνο υλοποίησης'],
    },
    {
      title: 'Σχεδιασμός εμπειρίας χρήστη',
      description: 'Σχεδιάζουμε την εφαρμογή ώστε να είναι απλή, καθαρή και εύχρηστη σε κάθε συσκευή.',
      bullets: ['Οθόνες και ροές χρήσης', 'Σχεδιασμός προσαρμοσμένος σε κινητό και υπολογιστή', 'Έγκαιρο feedback πριν την ανάπτυξη'],
    },
    {
      title: 'Ανάπτυξη & έλεγχος',
      description: 'Υλοποιούμε την εφαρμογή με έμφαση στη σταθερότητα και την απόδοση.',
      bullets: ['Καθαρός και οργανωμένος κώδικας', 'Έλεγχοι λειτουργίας πριν το launch', 'Ασφάλεια και σωστή διαχείριση δεδομένων'],
    },
    {
      title: 'Παράδοση & υποστήριξη',
      description: 'Η εφαρμογή παραδίδεται έτοιμη για χρήση και σας δείχνουμε πώς να τη διαχειρίζεστε.',
      bullets: ['Παράδοση λειτουργικής εφαρμογής', 'Βασική εκπαίδευση', 'Υποστήριξη μετά το λανσάρισμα'],
    },
  ],
};

const deliverablesCopy: Record<Locale, BulletSection> = {
  en: {
    eyebrow: 'Deliverables',
    heading: 'What we deliver',
    description: 'Every project includes everything you need to continue without dependencies.',
    bullets: [
      'Fully functional application',
      'Source code and technical documentation',
      'Configured runtime environment',
      'Usage guidance for your team',
    ],
  },
  el: {
    eyebrow: 'Τι παραδίδουμε',
    heading: 'Τι παραδίδουμε',
    description: 'Κάθε έργο συνοδεύεται από όλα όσα χρειάζεστε για να συνεχίσετε χωρίς εξαρτήσεις.',
    bullets: [
      'Πλήρως λειτουργική εφαρμογή',
      'Κώδικας και τεχνική τεκμηρίωση',
      'Ρυθμισμένο περιβάλλον λειτουργίας',
      'Οδηγίες χρήσης για την ομάδα σας',
    ],
  },
};

const audienceCopy: Record<Locale, BulletSection> = {
  en: {
    eyebrow: 'Who this is for',
    heading: 'Who this is for',
    description: 'This service is a fit if:',
    bullets: [
      'You need an application for your business',
      'You want something tailored, not an off-the-shelf solution',
      'You want to start simple and expand later',
      'You need technical guidance, not just development',
    ],
  },
  el: {
    eyebrow: 'Σε ποιους απευθύνεται',
    heading: 'Σε ποιους απευθύνεται',
    description: 'Η υπηρεσία αυτή είναι κατάλληλη αν:',
    bullets: [
      'Χρειάζεστε μια εφαρμογή για την επιχείρησή σας',
      'Θέλετε κάτι προσαρμοσμένο και όχι έτοιμη λύση',
      'Θέλετε να ξεκινήσετε απλά και να εξελιχθεί αργότερα',
      'Χρειάζεστε τεχνική καθοδήγηση και όχι απλώς ανάπτυξη',
    ],
  },
};

const faqIntroCopy: Record<Locale, SectionCopy> = {
  en: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    description: 'Everything you need to know before starting.',
  },
  el: {
    eyebrow: 'FAQ',
    heading: 'Συχνές ερωτήσεις',
    description: 'Όσα χρειάζεται να γνωρίζετε πριν ξεκινήσετε.',
  },
};

const faqCopy: Record<Locale, FaqItem[]> = {
  en: [
    {
      question: 'How long does an application take?',
      answer:
        'It depends on the features. We usually start with a basic version within a few weeks.',
    },
    {
      question: 'Can I start small?',
      answer:
        'Yes. Many projects start with a simple version and expand gradually.',
    },
    {
      question: 'Do you support web and mobile apps?',
      answer:
        'Yes. We can deliver web apps, mobile apps, or a combination of both.',
    },
    {
      question: 'Is there support after delivery?',
      answer:
        'Yes. We offer support and improvements after launch.',
    },
  ],
  el: [
    {
      question: 'Πόσο χρόνο χρειάζεται μια εφαρμογή;',
      answer:
        'Εξαρτάται από τις λειτουργίες. Συνήθως ξεκινάμε με μια βασική έκδοση μέσα σε λίγες εβδομάδες.',
    },
    {
      question: 'Μπορώ να ξεκινήσω με κάτι μικρό;',
      answer:
        'Ναι. Πολλά projects ξεκινούν με μια απλή έκδοση και επεκτείνονται σταδιακά.',
    },
    {
      question: 'Υποστηρίζετε web και mobile εφαρμογές;',
      answer:
        'Ναι. Μπορούμε να υλοποιήσουμε web εφαρμογές, mobile ή συνδυασμό και των δύο.',
    },
    {
      question: 'Υπάρχει υποστήριξη μετά την παράδοση;',
      answer:
        'Ναι. Προσφέρουμε υποστήριξη και βελτιώσεις μετά το launch.',
    },
  ],
};

const closingCopy: Record<Locale, ClosingContent> = {
  en: {
    eyebrow: 'Next step',
    title: 'Want to discuss if an app is the right solution for you?',
    primaryCta: 'Free consultation',
  },
  el: {
    eyebrow: 'Επόμενο βήμα',
    title: 'Θέλετε να συζητήσουμε αν μια εφαρμογή είναι η σωστή λύση για εσάς;',
    primaryCta: 'Δωρεάν συμβουλευτική',
  },
};

export default function MobileApp() {
  const { language } = useLanguage();
  const location = useLocation();
  const locale: Locale = language === 'el' ? 'el' : 'en';
  const localizedPath = useLocalizedPath();

  const metaData = getServiceMeta(serviceSlug, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const serviceDisplayName = locale === 'en' ? 'Web & Mobile App Development' : serviceName;

  const hero = heroCopy[locale];
  const appTypes = appTypesCopy[locale];
  const processIntro = processIntroCopy[locale];
  const processSteps = processCopy[locale];
  const deliverables = deliverablesCopy[locale];
  const audience = audienceCopy[locale];
  const faqIntro = faqIntroCopy[locale];
  const faqItems = faqCopy[locale];
  const closing = closingCopy[locale];
  const infoSections = [
    { data: deliverables, Icon: Wrench },
    { data: audience, Icon: Users },
  ];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    trackServiceView(serviceSlug, language);
    const cleanup = trackScroll({ threshold: 0.75, once: true });
    return cleanup;
  }, [language]);

  const handleCTA = (ctaText: string) => {
    trackCTA(ctaText, window.location.pathname);
    window.navigateToConsultation?.();
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  return (
    <>
      <Seo
        title={metaData.title}
        description={metaData.description}
        url={canonicalUrl}
        image={metaData.image}
        hreflang={hreflang}
      />

      <JsonLd json={generateOrganizationSchema()} />
      <JsonLd json={generateWebsiteSchema(locale)} />
      <JsonLd json={generateBreadcrumbSchema(serviceDisplayName, serviceSlug, locale)} />
      <JsonLd json={generateServiceSchema(serviceDisplayName, metaData.description, canonicalUrl, 'App Development')} />
      <JsonLd json={generateFAQSchema(faqItems)} />

      <div className="relative min-h-screen bg-[#0b0a0e] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10 overflow-hidden">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div
              className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black"
              aria-hidden="true"
            />
            <Container className="relative lg:min-h-[calc(100vh-220px)] lg:flex lg:flex-col lg:justify-center">
              <div className="max-w-3xl space-y-8">
                <div className="space-y-5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                    {hero.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                  >
                    {hero.title}
                  </Display>
                </div>

                <Display
                  variant="subhead"
                  as="p"
                  className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
                >
                  {hero.description}
                </Display>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                    onClick={() => handleCTA(hero.primaryCta)}
                  >
                    {hero.primaryCta}
                  </Button>
                  <Button
                    as="a"
                    href={localizedPath('/projects')}
                    variant="secondary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {hero.secondaryCta}
                  </Button>
                </div>
              </div>
            </Container>
          </HeroSection>

          <Section>
            <Container>
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1.05fr)] lg:items-start lg:gap-12">
                <div className="space-y-4">
                  {appTypes.eyebrow ? (
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                      {appTypes.eyebrow}
                    </p>
                  ) : null}
                  <h2 className="text-3xl font-semibold text-white md:text-5xl">{appTypes.heading}</h2>
                  <p className="text-lg text-white/80">{appTypes.description}</p>
                </div>

                <div className="hidden lg:block h-full w-px bg-gradient-to-b from-transparent via-white/10 to-transparent" />

                <Card className="relative overflow-hidden rounded-[28px] border-white/15 bg-black/70 backdrop-blur-xl p-6 sm:p-7 shadow-glass">
                  <div className="pointer-events-none absolute -right-24 -top-20 h-44 w-44 rounded-full bg-brand-highlight/15 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-24 -left-16 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
                  <div className="relative space-y-6">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">
                      {appTypes.rightTitle}
                    </p>
                    <ul className="space-y-4 text-sm text-white/70">
                      {appTypes.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3">
                          <span className="mt-2 h-2 w-2 rounded-full bg-brand-highlight/80" />
                          <span className="leading-relaxed">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              </div>
            </Container>
          </Section>

          <Section id="process" className="relative isolate">
            <div className="absolute inset-0 geometric-pattern opacity-20" aria-hidden="true" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4 text-center md:mx-auto">
                {processIntro.eyebrow ? (
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {processIntro.eyebrow}
                  </p>
                ) : null}
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{processIntro.heading}</h2>
                <p className="text-lg text-white/70">{processIntro.description}</p>
              </div>

              <div className="mt-16 grid gap-8 lg:grid-cols-2">
                {processSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="group flex h-full flex-col gap-4 rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-lg font-semibold text-white transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <h3 className="text-2xl font-semibold text-white">{step.title}</h3>
                    </div>
                    <p className="text-sm text-white/70">{step.description}</p>
                    <ul className="mt-4 space-y-2 text-sm text-white/70">
                      {step.bullets.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-highlight/70" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Container>
          </Section>

          <Section>
            <Container>
              <div className="grid gap-8 lg:grid-cols-2">
                {infoSections.map(({ data: section, Icon }) => (
                  <Card
                    key={section.heading}
                    className="space-y-5 rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-8"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-brand-highlight/80">
                      <Icon className="h-6 w-6" />
                    </div>
                    {section.eyebrow ? (
                      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                        {section.eyebrow}
                      </p>
                    ) : null}
                    <h2 className="text-2xl font-semibold text-white md:text-3xl">{section.heading}</h2>
                    <p className="text-sm text-white/70">{section.description}</p>
                    <ul className="space-y-3 text-sm text-white/75">
                      {section.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-start gap-3">
                          <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                          <span className="leading-relaxed">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
            </Container>
          </Section>

          <Section>
            <Container className="max-w-5xl">
              <div className="text-center">
                {faqIntro.eyebrow ? (
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                    {faqIntro.eyebrow}
                  </p>
                ) : null}
                <h2 className="mt-4 text-3xl font-semibold text-white md:text-5xl">{faqIntro.heading}</h2>
                <p className="mt-4 text-lg text-white/70">{faqIntro.description}</p>
              </div>

              <div className="mt-12 space-y-4">
                {faqItems.map((faq, index) => {
                  const open = openFaqIndex === index;
                  return (
                    <div
                      key={faq.question}
                      className="overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-base font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
                      >
                        <span>{faq.question}</span>
                        {open ? (
                          <ChevronUp className="h-5 w-5 text-white/60" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-white/60" />
                        )}
                      </button>
                      {open && (
                        <div className="border-t border-white/10 px-6 py-5 text-sm text-white/70">
                          <p className="leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />
                <div className="relative text-center">
                  {closing.eyebrow ? (
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                      {closing.eyebrow}
                    </p>
                  ) : null}
                  <Display
                    variant="h1"
                    as="h2"
                    className="mx-auto mt-4 max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
                  >
                    {closing.title}
                  </Display>
                  {closing.description ? (
                    <p className="mx-auto mt-4 max-w-3xl text-center text-lg text-white/70">
                      {closing.description}
                    </p>
                  ) : null}

                  <div className="mt-10 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                      onClick={() => handleCTA(closing.primaryCta)}
                    >
                      {closing.primaryCta}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
